import { Global, css } from "@emotion/react";
import { format } from "date-fns";
import { useEffect, useState, useRef, useCallback, useContext } from "react";
import { createPortal } from "react-dom";
import {
  alpha,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import StyledCalendar from "./StyledCalendar";
import CalendarToolbar, { VIEW_MODE_OPTIONS, VIEW_OPTIONS } from "./CalendarToolbar";
import useDebounce from "../../hooks/useDebounce";
import NewClosingModal from "./NewClosingModal";
import { useCalendar } from "../contexts/CalendarContext";
import CalenderEventContent from "./CalendarEventContent";
import ViewClosingModal from "./ViewClosingModal";
import ConfirmDateDragModal from "./ConfirmDateDragModal";
import { logEvent } from "../utils/analyticsLogger";
import { useSettingsContext } from "../../minimals/components/settings";
import useLocalStorage from "../../minimals/hooks/useLocalStorage";
import Iconify from "../../minimals/components/iconify";
import Page from "../../minimals/components/page";
import { DatesSetArg, EventContentArg, EventDropArg } from "@fullcalendar/core";
import FullCalendar from "@fullcalendar/react";
import DotLoader from "../components/DotLoader";
import HeaderBreadcrumbs from "../components/HeaderBreadcrumbs";
import ToastMessage from "../components/ToastMessage";
import { useQueryParam, StringParam, BooleanParam } from "use-query-params";
import { IClosedOrder, ICalendarEvent } from "../types/calendar";
import { getClosedOrder } from "./CalendarActions";
import useResponsive from "../../minimals/hooks/useResponsive";
import { HeaderSlotContext } from "../components/layouts/dashboard/HeaderSlotContext";
import CalendarOrderSearch from "./CalendarOrderSearch";
import CalendarFilters from "./CalendarFilters";
import { compareCalendarEvents, getCalendarEventDate, statusColorMap } from "./CalendarUtils";

const floatingBarButtonSx = {
  flexShrink: 0,
  width: 40,
  height: 40,
  color: "primary.dark",
  backgroundColor: "background.paper",
  border: "1px solid",
  borderColor: "rgba(145, 158, 171, 0.48)",
  boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)",
  "&:hover": { backgroundColor: "background.paper" }
};

export default function Calendar() {
  const { themeStretch } = useSettingsContext();
  const isMobile = useResponsive("down", "md");
  const { slotEl } = useContext(HeaderSlotContext);
  // Owned here so both the toolbar button and the floating bar can open it.
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [viewMenuAnchor, setViewMenuAnchor] = useState<HTMLElement | null>(null);
  const [viewModeMenuAnchor, setViewModeMenuAnchor] = useState<HTMLElement | null>(null);
  const [daySheetDate, setDaySheetDate] = useState<Date | null>(null);
  // Set when an order is opened from the day list, which is what enables the
  // modal's "back to list" button. Close goes to the calendar instead.
  const [daySheetReturnDate, setDaySheetReturnDate] = useState<Date | null>(null);
  const hadModalOrderRef = useRef(false);
  const [orderNumberUrlParam, setOrderNumberUrlParam] = useQueryParam("ordernumber", StringParam);
  const [modalModeParam] = useQueryParam("mode", StringParam);
  // const isDesktop = useResponsive('up', 'sm');
  const [isNewClosingModalOpen, setIsNewClosingModalOpen] = useState(false);
  const [isDisplayConfirmDragModal, setIsDisplayConfirmDragModal] = useState(false);
  const [isExpandedView, setIsExpandedView] = useState(false);
  const [isDisplayWeekends, setIsDisplayWeekends] = useLocalStorage<boolean>("isDisplayWeekends", false);
  const [date, setDate] = useState(new Date());
  // Resolved synchronously at mount: FullCalendar reads initialView once, and
  // useMediaQuery isn't guaranteed to have settled on the first render.
  const theme = useTheme();
  const [isMobileAtMount] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(theme.breakpoints.down("md").replace("@media ", "")).matches
  );
  // Mobile keeps its own key so choosing a view here never changes the desktop one.
  const [view, setView] = useLocalStorage<string>(
    isMobileAtMount ? "calendar-view-mobile" : "calendar-view",
    "dayGridMonth"
  );
  const [isLoading, setIsLoading] = useState(false);
  const activeViewOption = VIEW_OPTIONS.find((option) => option.id === view);
  const {
    calendarEvents,
    modalOrder,
    getAndSetClosedOrders,
    dateRange,
    setDateRange,
    toastProps,
    openViewOrderModal,
    showToastMessage,
    viewMode,
    setViewMode,
    setModalOrder,
    selectedPropertyType,
    selectedStates,
    selectedStatuses,
    selectedSalesReps,
    selectedAccountReps
  } = useCalendar();

  // One per active filter group, matching how /documents counts them — totalling
  // every selection would put "23" on the badge the moment someone picks states.
  const activeFilterCount =
    (selectedPropertyType ? 1 : 0) +
    (selectedStates.length ? 1 : 0) +
    (selectedStatuses.length ? 1 : 0) +
    (selectedSalesReps.length ? 1 : 0) +
    (selectedAccountReps.length ? 1 : 0);
  const activeViewModeOption = VIEW_MODE_OPTIONS.find((option) => option.value === viewMode);
  const shiftedDaySheetDate = (days: number) => {
    if (!daySheetDate) return null;
    const next = new Date(daySheetDate);
    next.setDate(next.getDate() + days);
    // Only the visible range is loaded, so paging past it would render a
    // misleading "no closings" rather than data we simply do not have.
    // start/end are null until the first datesSet fires.
    if (!dateRange.start || !dateRange.end) return null;
    return next >= new Date(dateRange.start) && next <= new Date(dateRange.end) ? next : null;
  };
  const previousDaySheetDate = shiftedDaySheetDate(-1);
  const nextDaySheetDate = shiftedDaySheetDate(1);
  const daySheetKey = daySheetDate ? format(daySheetDate, "yyyy-MM-dd") : "";
  // Sorted with the shared comparator so row N here is the "N/total" the view
  // modal reports after you tap into it.
  const daySheetEvents = daySheetKey
    ? calendarEvents
        .filter((event) => {
          const eventDate = getCalendarEventDate(event);
          return eventDate ? format(eventDate, "yyyy-MM-dd") === daySheetKey : false;
        })
        .sort(compareCalendarEvents)
    : [];
  const debouncedDateRange = useDebounce(`${dateRange.start}-${dateRange.end}`, 500);

  useEffect(() => {
    const hadModalOrder = hadModalOrderRef.current;
    hadModalOrderRef.current = Boolean(modalOrder);

    // Forget the origin once the modal is gone, so an order opened later from a
    // URL or search doesn't offer a back button to an unrelated day.
    if (hadModalOrder && !modalOrder) {
      setDaySheetReturnDate(null);
    }
  }, [modalOrder]);
  const calendarDragEventData = useRef<any>({});
  const calendarRef = useRef<FullCalendar>(null);
  const startDateRef = useRef("");
  const endDateRef = useRef("");

  const isMonthView = view === "dayGridMonth" || view === "dayGridMonthExpanded";

  const handleDateClick = useCallback(
    (info: any) => {
      // Mobile month view: the cell is too narrow to read, so any tap opens the
      // day's full list rather than sending the user off to the day view.
      if (isMobile && isMonthView) {
        setDaySheetDate(info.date);
        return;
      }

      if (!info.jsEvent.target.classList.contains("fc-daygrid-day-number")) return;
      gotToDateInListView(info.date);
    },
    [isMobile, isMonthView]
  );

  const scrollToToday = () => {
    const todayEl = document.querySelector(".fc-day-today");
    //This is not ideal, but I could not find anywhere in the docs that allowed for scrolling to to today
    todayEl?.scrollIntoView(false); ///To have scroll view on top of page set param to true or remove alogether
  };

  const getOrdersWithLoader = () => {
    setIsLoading(true);
    ///Doing this for bug when turning off show weekends on day view when currently selecting day view
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      setDate(calendarApi.getDate());
    }

    getAndSetClosedOrders()
      .then(() => setIsLoading(false))
      .catch((e) => {
        console.log("Error", e);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getOrdersWithLoader();
  }, [debouncedDateRange]);

  useEffect(() => {
    setTimeout(scrollToToday, 0);

    const handleOrderNumberQueryParam = async () => {
      if (orderNumberUrlParam) {
        const { data }: { data: IClosedOrder } = await getClosedOrder(orderNumberUrlParam);
        if (!data) {
          showToastMessage({
            message: `There is an issue with editing order ${orderNumberUrlParam}`,
            severity: "error"
          });
          return;
        }
        openViewOrderModal({ order: data as ICalendarEvent, mode: modalModeParam == "edit" ? "edit" : "view" });
      }
    };

    handleOrderNumberQueryParam();
  }, []);

  const onEventDrop = async (info: EventDropArg) => {
    calendarDragEventData.current = info;
    setIsDisplayConfirmDragModal(true);
  };

  const handleDateSet = async (args: DatesSetArg) => {
    const { startStr, endStr, start, end } = args;
    if (startStr === startDateRef.current && endStr === endDateRef.current) {
      return;
    }

    startDateRef.current = startStr;
    endDateRef.current = endStr;
    setIsLoading(true);
    setDateRange({ start, end });
  };

  const handleChangeView = (selectedViewOption: {
    id: any;
    value: any;
    label?: string;
    icon?: string;
    isExpanded: any;
  }) => {
    const newView = selectedViewOption.value;
    setIsExpandedView(selectedViewOption.isExpanded || false);
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.changeView(newView);
      setView(selectedViewOption.id);
    }
  };

  const handleClickToday = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.today();
      setDate(calendarApi.getDate());
    }
  };

  const handleClickDatePrev = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.prev();
      setDate(calendarApi.getDate());
    }
  };

  const handleClickDateNext = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.next();
      setDate(calendarApi.getDate());
    }
  };

  const handleGoToDate = (date: string | Date) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.gotoDate(date);
      calendarApi.select({ start: date });
      setDate(calendarApi.getDate());
    }

    const selectedEl = document.querySelector(".fc-highlight"); //This is not ideal, but I could not find anywhere in the docs that allowed for scrolling to to today
    selectedEl?.scrollIntoView(); ///To have scroll view on top of page set param to true or remove alogether
  };

  const gotToDateInListView = (date: Date) => {
    handleChangeView({
      id: "listWeekOneDay",
      value: "listWeekOneDay",
      label: "Day",
      icon: "ic:round-view-day",
      isExpanded: false
    });
    handleGoToDate(date);
  };

  const handleToggleDisplayWeekends = (isChecked: boolean) => {
    logEvent("Closing Calendar", "Filter 2", `Show weekends ${isChecked}`);
    setIsDisplayWeekends(isChecked);
    // const calendarEl = calendarRef.current;
    // if (calendarEl) {
    // const calendarApi = calendarEl.getApi();
    // setTimeout(() => setDate(calendarApi.getDate()), 50)
    // }
  };

  const renderDayCellContent = (e: any) => {
    if (!isLoading) {
      return <>{e.dayNumberText}</>;
    }

    return (
      <div>
        <Stack spacing={0.5}>
          <Skeleton variant="text" sx={{ fontSize: "1rem" }} width={215} />
          <Skeleton variant="text" sx={{ fontSize: "1rem" }} width={215} />
          <Skeleton variant="text" sx={{ fontSize: "1rem" }} width={215} />
          <Skeleton variant="text" sx={{ fontSize: "1rem" }} width={215} />
        </Stack>
      </div>
    );
  };

  return (
    <Page title="Calendar">
      <Global
        styles={css`
          header {
            background-color: ${isMobile ? "inherit" : "transparent !important"};
            backdrop-filter: ${isMobile ? "inherit" : "none !important"};
          }
          main {
            /* Mobile drops the heading Container, which was supplying the clearance
               under the fixed 64px header — replace it here or content tucks under.
               64px is exactly the header height, so this is the floor. */
            padding-top: ${isMobile ? "64px" : "24px"} !important;
            padding-bottom: 40px !important;
          }
          ${
            isMobile
              ? `/* The calendar runs height="auto", so the loading / no-events box
                    shrinks to its text and leaves dead space below it. Offset covers
                    the header, toolbar, and the floating bar. */
                 .fc .fc-list-empty {
                   min-height: calc(100vh - 200px);
                 }

                 /* Day cells clip their contents. The custom eventContent renders
                    into .fc-event-main, not .fc-event-title, so the theme's existing
                    ellipsis rule never applies to it — hence text bleeding across
                    columns. Truncate here instead of only clipping. */
                 .fc .fc-daygrid-day-frame {
                   overflow: hidden;
                 }
                 .fc .fc-daygrid-event .fc-event-main {
                   display: block;
                   overflow: hidden;
                   white-space: nowrap;
                   text-overflow: ellipsis;
                 }

                 /* Events read as a density preview only — the whole cell is the
                    tap target, opening a legible day sheet instead. */
                 .fc .fc-daygrid-event,
                 .fc .fc-daygrid-more-link {
                   pointer-events: none;
                 }
                 .fc .fc-daygrid-day-frame {
                   cursor: pointer;
                 }
                 /* !important, not a longer selector: StyledCalendar sets this
                    through a 3-class rule (0,3,0) that any selector here ties with
                    at best, and emotion's Global/styled order is not guaranteed. */
                 .fc .fc-daygrid-day-frame {
                   min-height: 88px !important;
                 }

                 /* Same story: StyledCalendar sets this at (0,4,0). */
                 .fc .fc-col-header-cell-cushion {
                   padding: 8px 0 !important;
                 }`
              : ""
          }
        `}
      />

      {/* Mobile puts the heading and New Closing into the site header, beside the
          nav button, instead of a full breadcrumb block that eats vertical space. */}
      {isMobile && slotEl
        ? // gap, not `spacing` — Stack's spacing emits a margin rule that would
          // outrank the trailing group's auto margin.
          createPortal(
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: 1, minWidth: 0, gap: 1 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: "primary.dark", fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}>
                  Calendar
                </Typography>
                {calendarEvents.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
                    {calendarEvents.length} Closings
                  </Typography>
                )}
              </Box>
              <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, gap: 1.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setIsNewClosingModalOpen(true)}
                  startIcon={<Iconify icon={"eva:plus-fill"} width={16} />}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  New Closing
                </Button>
              </Stack>
            </Stack>,
            slotEl
          )
        : null}

      {!isMobile && (
        <Container
          maxWidth={themeStretch ? false : "xl"}
          sx={{
            paddingLeft: "0px !important",
            paddingRight: "64px !important",
            top: 24,
            position: "relative",
            paddingTop: 10
          }}
        >
          <HeaderBreadcrumbs
            sx={{ height: 66 }}
            heading="Calendar"
            subheading={calendarEvents.length ? `${calendarEvents.length} Closings` : ""}
            links={[{ name: "Dashboard", href: "" }, { name: "User", href: "" }, { name: "List" }]}
            action={
              <Button
                variant="contained"
                onClick={() => setIsNewClosingModalOpen(true)}
                startIcon={<Iconify icon={"eva:plus-fill"} />}
              >
                New Closing
              </Button>
            }
          />
        </Container>
      )}
      <StyledCalendar sx={{ position: "relative" }}>
        <CalendarToolbar
          date={date}
          view={view}
          onNextDate={handleClickDateNext}
          onPrevDate={handleClickDatePrev}
          onToday={handleClickToday}
          onChangeView={handleChangeView}
          onGoToDate={handleGoToDate}
          isDisplayWeekends={isDisplayWeekends}
          handleToggleDisplayWeekends={handleToggleDisplayWeekends}
          onOpenFilters={() => setIsFilterDrawerOpen(true)}
        />
        <FullCalendar
          dayCellContent={renderDayCellContent}
          datesSet={handleDateSet}
          showNonCurrentDates={true}
          weekends={isDisplayWeekends}
          editable
          droppable
          selectable
          allDayMaintainDuration
          eventResizableFromStart
          fixedWeekCount={false}
          ref={calendarRef}
          initialDate={date}
          initialView={view}
          dayMaxEventRows={isExpandedView || view !== "dayGridMonth" ? Infinity : 5}
          eventDisplay="list-item"
          headerToolbar={false}
          events={calendarEvents as any}
          eventContent={(eventInfo: EventContentArg) => (
            <CalenderEventContent isLoading={isLoading} eventInfo={eventInfo} view={view} />
          )}
          contentHeight={isMobile ? "auto" : 3000}
          eventMinHeight={100}
          eventDurationEditable={false}
          eventDrop={onEventDrop}
          views={{
            ///For custom views
            dayGridMonthExpanded: {
              type: "dayGridMonth",
              buttonText: "Day Grid Expanded"
            },
            listWeekOneDay: {
              type: "listWeek",
              duration: { days: 1 },
              buttonText: "1 day"
            }
          }}
          noEventsContent={
            isLoading ? (
              <>
                Loading
                <DotLoader />
              </>
            ) : undefined
          }
          dateClick={(info) => handleDateClick(info)}
          // navLinkDayClick
          // dayClick={info => gotToDateInListView(info.date)}
          height={"auto"}
          plugins={[listPlugin, dayGridPlugin, timeGridPlugin, interactionPlugin]}
        />
      </StyledCalendar>
      {/* Mobile filter drawer, opened from the toolbar or the floating bar. */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          PaperProps={{
            sx: {
              maxHeight: "85vh",
              borderRadius: "12px 12px 0 0",
              pb: "env(safe-area-inset-bottom)"
            }
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.25, borderBottom: "1px solid", borderColor: "divider" }}
          >
            <Typography sx={{ fontWeight: 700 }}>Filters</Typography>
            <IconButton size="small" onClick={() => setIsFilterDrawerOpen(false)}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Stack>
          <Box
            sx={{
              p: 2,
              overflowY: "auto",
              overscrollBehavior: "contain",
              // Theme outlines are grey 500 at 32% alpha, which nearly disappears on
              // a phone screen. Skip focused/error inputs so they keep their colour.
              "& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
                borderColor: "grey.500"
              }
            }}
          >
            <CalendarFilters onGoToDate={handleGoToDate} hideOrderSearch />
          </Box>
        </Drawer>
      )}

      {/* Same floating bar pattern as /documents: a nav aid on the left, the search
          pill in the middle, and a context action on the right. */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (theme) => theme.zIndex.drawer - 1,
            px: 1.5,
            pt: 4,
            pb: "calc(12px + env(safe-area-inset-bottom))",
            display: "flex",
            justifyContent: "center",
            pointerEvents: "none",
            // Gradient scrim rather than a frosted panel: it fades out with no edge,
            // so it stops rows showing through the gaps between the pills without
            // reading as a second rounded layer behind them.
            background: (theme) =>
              `linear-gradient(to top, ${theme.palette.background.default} 55%, ${alpha(
                theme.palette.background.default,
                0
              )})`
          }}
        >
          {/* Bar spans the available width so the search can flex; the buttons hold
              their size and the field absorbs whatever is left. */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ pointerEvents: "auto", width: 1, maxWidth: 520, minWidth: 0 }}
          >
            <Badge
              badgeContent={activeFilterCount}
              color="primary"
              overlap="circular"
              sx={{ flexShrink: 0 }}
            >
              <IconButton
                aria-label={`Filters${activeFilterCount ? ` (${activeFilterCount} active)` : ""}`}
                onClick={() => setIsFilterDrawerOpen(true)}
                sx={floatingBarButtonSx}
              >
                <Iconify icon="ic:round-filter-list" width={22} />
              </IconButton>
            </Badge>

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 999,
                  backgroundColor: "background.paper",
                  boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)"
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => alpha(theme.palette.grey[500], 0.48)
                }
              }}
            >
              {/* Empty label so it reads as a pill, like the documents search. */}
              <CalendarOrderSearch
                onGoToDate={handleGoToDate}
                textboxLabel=""
                textboxPlaceholder="Search by order #"
              />
            </Box>

            <IconButton
              // The glyph mirrors the active view so the closed button still says
              // which one is on, without a badge to decode.
              aria-label={`Change view (currently ${activeViewOption?.label ?? "Calendar"})`}
              onClick={(event) => setViewMenuAnchor(event.currentTarget)}
              sx={floatingBarButtonSx}
            >
              <Iconify icon={activeViewOption?.icon ?? "eva:calendar-outline"} width={22} />
            </IconButton>

            <IconButton
              aria-label={`Group by (currently ${activeViewModeOption?.label ?? "Client"})`}
              onClick={(event) => setViewModeMenuAnchor(event.currentTarget)}
              sx={{
                ...floatingBarButtonSx,
                width: "auto",
                minWidth: 92,
                px: 1.5,
                borderRadius: 999
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1, whiteSpace: "nowrap" }}>
                {activeViewModeOption?.label ?? "Client"}
              </Typography>
            </IconButton>
          </Stack>
        </Box>
      )}

      {isMobile && (
        <Menu
          anchorEl={viewMenuAnchor}
          open={Boolean(viewMenuAnchor)}
          onClose={() => setViewMenuAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
          // MenuItem re-asserts a 36px min-width on ListItemIcon, outranking the
          // theme's `minWidth: auto`. Undo it so the icon box is icon-width and the
          // theme's 16px marginRight is the whole gap.
          sx={{ "& .MuiListItemIcon-root": { minWidth: "auto" } }}
        >
          {VIEW_OPTIONS.map((viewOption) => (
            <MenuItem
              key={viewOption.id}
              selected={viewOption.id === view}
              onClick={() => {
                handleChangeView(viewOption);
                setViewMenuAnchor(null);
              }}
            >
              <ListItemIcon>
                <Iconify icon={viewOption.icon} width={20} />
              </ListItemIcon>
              <ListItemText>{viewOption.label}</ListItemText>
            </MenuItem>
          ))}
          <Divider />
          <MenuItem
            onClick={() => {
              handleClickToday();
              setViewMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <Iconify icon="eva:radio-button-on-outline" width={20} />
            </ListItemIcon>
            <ListItemText>Go to today</ListItemText>
          </MenuItem>
        </Menu>
      )}

      {isMobile && (
        <Menu
          anchorEl={viewModeMenuAnchor}
          open={Boolean(viewModeMenuAnchor)}
          onClose={() => setViewModeMenuAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          {VIEW_MODE_OPTIONS.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === viewMode}
              onClick={() => {
                logEvent("Closing Calendar", "Filter 2", option.value);
                setViewMode(option.value);
                setViewModeMenuAnchor(null);
              }}
            >
              <ListItemText>{option.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      )}

      {isMobile && (
        <Drawer
          anchor="bottom"
          open={Boolean(daySheetDate)}
          onClose={() => setDaySheetDate(null)}
          PaperProps={{
            sx: {
              display: "flex",
              flexDirection: "column",
              // Sizes to its content; the cap keeps the month visible behind it and
              // hands overflow to the list. No floor — the bottom bar is anchored,
              // so a short day just means a shorter sheet, not a moving target.
              maxHeight: "80vh",
              borderRadius: "12px 12px 0 0",
              pb: "env(safe-area-inset-bottom)"
            }
          }}
        >

          {/* Bar sits at the bottom: the sheet grows upward from a fixed bottom
              edge, so only the bottom is stable while paging between days. */}
          {/* overscrollBehavior stops a flick that runs past the end of the list from
              chaining to the document — on iOS that chained scroll is what makes
              Safari's toolbar expand. */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
            {daySheetEvents.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
                No closings on this day.
              </Typography>
            ) : (
              daySheetEvents.map((event) => (
                <Box
                  key={`${event.orderNumber}-${String(event.start)}`}
                  component="button"
                  type="button"
                  onClick={() => {
                    // Same handoff the in-cell event used to perform.
                    logEvent("Closing Calendar", "View Closing", "");
                    setDaySheetReturnDate(daySheetDate);
                    setOrderNumberUrlParam(event.orderNumber);
                    setModalOrder(event);
                    setDaySheetDate(null);
                  }}
                  sx={{
                    width: "100%",
                    border: 0,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "background.paper",
                    font: "inherit",
                    textAlign: "left",
                    cursor: "pointer",
                    px: 2,
                    py: 1.25,
                    "&:last-of-type": { borderBottom: 0 }
                  }}
                >
                  <Stack direction="row" alignItems="flex-start" sx={{ gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: statusColorMap[event.status] || statusColorMap["default"],
                          fontSize: 14,
                          fontWeight: 700
                        }}
                      >
                        {event.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {viewMode === "client" && event.clientName}
                        {viewMode === "rep" && `${event.accountRep || "..."} / ${event.funder || "..."}`}
                        {viewMode === "titlecloser" && event.titleCloser}
                      </Typography>
                    </Box>

                    {/* The row had dead space on the right; status is the thing you
                        actually triage on, with type/state as supporting context. */}
                    <Stack sx={{ flexShrink: 0, alignItems: "flex-end", maxWidth: "42%" }}>
                      {event.status && (
                        <Typography
                          sx={{
                            color: statusColorMap[event.status] || statusColorMap["default"],
                            fontSize: 11,
                            fontWeight: 700,
                            whiteSpace: "nowrap"
                          }}
                        >
                          {event.status}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 1 }}
                      >
                        {[event.transactionType, event.state].filter(Boolean).join(" · ")}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              ))
            )}
          </Box>

          {/* Grid, not flex: the equal 1fr side columns centre the date against the
              bar itself, rather than against whatever space the controls leave. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              gap: 1,
              px: 1,
              py: 1.25,
              flexShrink: 0,
              borderTop: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper"
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              // pl brings it to the rows' 16px inset — the bar itself sits at 8px so
              // the icon buttons keep their own edge spacing.
              sx={{ pl: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {daySheetEvents.length} {daySheetEvents.length === 1 ? "closing" : "closings"}
            </Typography>

            {/* Arrows flank the date, matching the month nav in the toolbar. */}
            <Stack direction="row" alignItems="center" sx={{ gap: 0.5 }}>
              <IconButton
                size="small"
                aria-label="Previous day"
                disabled={!previousDaySheetDate}
                onClick={() => setDaySheetDate(previousDaySheetDate)}
              >
                <Iconify icon="eva:arrow-ios-back-fill" />
              </IconButton>
              <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3, whiteSpace: "nowrap" }}>
                {daySheetDate ? format(daySheetDate, "EEE, MMM d, yyyy") : ""}
              </Typography>
              <IconButton
                size="small"
                aria-label="Next day"
                disabled={!nextDaySheetDate}
                onClick={() => setDaySheetDate(nextDaySheetDate)}
              >
                <Iconify icon="eva:arrow-ios-forward-fill" />
              </IconButton>
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="flex-end">
              <IconButton size="small" onClick={() => setDaySheetDate(null)}>
                <Iconify icon="eva:close-fill" />
              </IconButton>
            </Stack>
          </Box>
        </Drawer>
      )}

      {Boolean(modalOrder) && (
        <ViewClosingModal
          getOrdersWithLoader={getOrdersWithLoader}
          onBackToList={daySheetReturnDate ? () => setDaySheetDate(daySheetReturnDate) : undefined}
        />
      )}
      {isNewClosingModalOpen && <NewClosingModal isOpen={isNewClosingModalOpen} setIsOpen={setIsNewClosingModalOpen} />}
      {isDisplayConfirmDragModal && (
        <ConfirmDateDragModal
          info={calendarDragEventData.current}
          isOpen={isDisplayConfirmDragModal}
          setIsOpen={setIsDisplayConfirmDragModal}
          resetDragEventData={() => (calendarDragEventData.current = {})}
        />
      )}
      {toastProps?.open && <ToastMessage {...toastProps} />}
    </Page>
  );
}
